import { api } from './api';

/**
 * Контекст вошедшего пользователя wiki. Может быть либо CRM-партнёром,
 * либо сотрудником iiko, либо вообще без авторизации.
 *
 * Возвращается из GET /api/wiki-auth/me.
 */
export interface WikiMeDto {
  type: 'crm_user' | 'iiko_employee';
  /** Только для CRM-юзера — нужен для аватара/profile-ссылки. */
  userId?: string;
  fullName: string;
  email?: string;
  /** Для CRM-юзера — URL `/api/users/avatar/<id>` если загружен. */
  avatarUrl?: string | null;
  roleCode?: string | null;
  roleName?: string | null;
  tradingPointName?: string | null;
  mustChangePassword?: boolean;
  canEdit: boolean;
  canCreate: boolean;
  canAdmin: boolean;
  /** Проверяет открытые ответы в тестах (фаза 2.3). */
  canReview?: boolean;
  /** Публикует статьи (kb:publish/kb:admin, фаза 3.5); иначе — только «На проверку». */
  canPublish?: boolean;
  /**
   * Загружает пакеты SCORM — только владелец сети (право '*'), не kb:admin (аудит безопасности
   * 31.10.2026, R1: скрипты курса исполняются с сессией зрителя). Без флага кнопка загрузки выключена.
   */
  canUploadScorm?: boolean;
  /**
   * На сервере поднят Hocuspocus — можно пробовать совместное редактирование
   * (фаза 4.7). Доступность WS через nginx флаг не гарантирует: не дождавшись
   * первого sync, клиент уходит в офлайн-режим (см. editor/useCollab.ts).
   */
  collabEnabled?: boolean;
  /**
   * «Смотреть от лица партнёра» (владелец 07.10.2026): настоящий пользователь
   * может смотреть вики от лица других ролей — в меню профиля есть пункт.
   */
  canViewAs?: boolean;
  /** Видит открытия партнёров в «Открытии точки» (право CRM «Этапы открытия — просмотр», не в режиме «от лица»). */
  canViewOpenings?: boolean;
  /**
   * «Досье партнёра» (FB-2, 31.10.2026): mine — партнёр заполняет «Моё досье»; network — партнёр или сотрудник УК читает
   * «Партнёры сети». Только для меню — сервер проверяет доступ сам.
   */
  dossier?: { mine: boolean; network: boolean };
  /** Режим просмотра включён: от лица кого смотрим. Только чтение. */
  viewAs?: WikiViewAsInfo | null;
}

export interface WikiViewAsInfo {
  roleName: string;
  roleCode: string;
  /** ИП, от лица которого смотрим (роль партнёра), иначе null. */
  partnerName: string | null;
  /** «От лица пользователя» (владелец 12.10.2026, включается в CRM): ФИО; null — режим роли. */
  userName?: string | null;
  /** Настоящий пользователь — для «Вернуться к себе». */
  realName: string;
}

export interface ViewAsRoleOption {
  id: string;
  name: string;
  code: string;
  /** Для роли партнёра выбирается ИП. */
  isPartner: boolean;
}

/**
 * «Смотреть от лица роли / партнёра» — те же эндпоинты, что у CRM
 * (backend/src/modules/auth/view-as.controller.ts): режим живёт в общей
 * cookie-сессии, поэтому включённый здесь действует и в CRM, и наоборот.
 */
export const viewAsApi = {
  /** Роли, от лица которых настоящему пользователю можно смотреть. */
  options: () =>
    api.get<{ roles: ViewAsRoleOption[]; active: { roleId: string; partnerId: string | null } | null }>('/auth/view-as/options'),
  /** ИП для роли партнёра — поиск по названию. */
  partners: (q: string) =>
    api.get<Array<{ id: string; name: string; isActive: boolean }>>(
      `/auth/view-as/partners${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  start: (roleId: string, partnerId?: string) =>
    api.post<{ ok: true }>('/auth/view-as', { roleId, ...(partnerId ? { partnerId } : {}) }),
  /** Выйти из режима просмотра (ответ 204 без тела). */
  stop: () => api.delete<void>('/auth/view-as'),
};

export const authApi = {
  /** Проверить текущую сессию. Кидает ApiError(401) если не залогинен. */
  me: () => api.get<WikiMeDto>('/wiki-auth/me'),

  /** Логин сотрудника iiko по телефону и паролю. */
  login: (phone: string, password: string) =>
    api.post<{ accountId: string; fullName: string; mustChangePassword: boolean }>(
      '/wiki-auth/login',
      { phone, password },
    ),

  /** Выход. Удаляет cookie. Безопасен даже если сессии нет. */
  logout: () => api.post<{ ok: true }>('/wiki-auth/logout'),

  /** Смена пароля сотрудника (после первого входа). */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>('/wiki-auth/change-password', {
      currentPassword,
      newPassword,
    }),
};
